/**
 * Skin NFT page — browse, purchase, and equip cosmetics for OrobasHipercube.
 *
 * Displays owned and available skins grouped by slot (hat, glasses, eyes, nose, mouth, body).
 * Purchases go through the same checkout flow as plans. Equipped skins sync to the macOS app.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Check, ShoppingBag, Sparkles, Crown, Eye, User, Package } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { HipercubeLottie, type HipercubePlan } from './HipercubeLottie';
import { fetchSkins, fetchUserSkins, equipSkin, fetchEquippedSkins, purchaseSkin, type Skin, type SkinSlot, type EquippedSkins, type UserSkinInventory } from '../lib/skins';

type SkinWithStatus = Skin & { equipped: boolean };

const SLOT_ORDER: SkinSlot[] = ['hat', 'glasses', 'eye', 'nose', 'mouth', 'body'];

const SLOT_ICONS: Record<SkinSlot, React.ComponentType<{ className?: string }>> = {
  hat: Crown,
  glasses: Eye,
  eye: Eye,
  nose: User,
  mouth: User,
  body: Package,
};

const SLOT_LABEL_KEYS: Record<SkinSlot, keyof ReturnType<typeof useI18n>['t']['skinNFT']> = {
  hat: 'slotHat',
  glasses: 'slotGlasses',
  eye: 'slotEye',
  nose: 'slotNose',
  mouth: 'slotMouth',
  body: 'slotBody',
};

const RARITY_COLORS: Record<Skin['rarity'], string> = {
  common: 'text-muted',
  rare: 'text-blue-400',
  epic: 'text-purple-400',
  legendary: 'text-gold',
};

const RARITY_BG: Record<Skin['rarity'], string> = {
  common: 'bg-white/5',
  rare: 'bg-blue-500/10 border-blue-500/20',
  epic: 'bg-purple-500/10 border-purple-500/20',
  legendary: 'bg-gold/10 border-gold/20',
};

export const SkinNFTView: React.FC = () => {
  const { status, session } = useAuth();
  const { t } = useI18n();
  const [allSkins, setAllSkins] = useState<readonly Skin[]>([]);
  const [userSkins, setUserSkins] = useState<readonly UserSkinInventory[]>([]);
  const [equipped, setEquipped] = useState<EquippedSkins>({
    hat: null, glasses: null, eye: null, nose: null, mouth: null, body: null,
  });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [equipping, setEquipping] = useState<string | null>(null);
  const [previewPlan, setPreviewPlan] = useState<HipercubePlan>('default');

  const loadData = useCallback(async () => {
    if (status !== 'signed-in' || !session) return;

    try {
      setLoading(true);
      const [catalogue, inventory, equippedRes] = await Promise.all([
        fetchSkins(),
        fetchUserSkins(session.accessToken),
        fetchEquippedSkins(session.accessToken),
      ]);
      setAllSkins(catalogue.skins);
      setUserSkins(inventory.skins);
      setEquipped(equippedRes.equipped);
    } catch (error) {
      console.error('Failed to load skins:', error);
    } finally {
      setLoading(false);
    }
  }, [status, session]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build skin list with ownership/equipped status
  const skinsWithStatus = React.useMemo((): SkinWithStatus[] => {
    const ownedIds = new Set(userSkins.map(s => s.skinId));
    const equippedIds = new Set(Object.values(equipped).filter(Boolean));
    return allSkins.map(skin => ({
      ...skin,
      isOwned: ownedIds.has(skin.id),
      equipped: equippedIds.has(skin.id),
    }));
  }, [allSkins, userSkins, equipped]);

  // Group by slot
  const skinsBySlot = React.useMemo(() => {
    const grouped: Record<SkinSlot, SkinWithStatus[]> = {
      hat: [], glasses: [], eye: [], nose: [], mouth: [], body: [],
    };
    for (const skin of skinsWithStatus) {
      grouped[skin.slot].push(skin);
    }
    return grouped;
  }, [skinsWithStatus]);

  const handleEquip = async (skin: SkinWithStatus) => {
    if (!session) return;
    const nextEquipped = !skin.equipped;
    setEquipping(skin.id);
    try {
      await equipSkin(session.accessToken, skin.id, nextEquipped);
      setEquipped(prev => {
        const next = { ...prev };
        if (nextEquipped) {
          next[skin.slot] = skin.id;
        } else if (next[skin.slot] === skin.id) {
          next[skin.slot] = null;
        }
        return next;
      });
      setUserSkins(prev => prev.map(s =>
        s.skinId === skin.id ? { ...s, isEquipped: nextEquipped } : s
      ));
    } catch (error) {
      console.error('Failed to equip skin:', error);
    } finally {
      setEquipping(null);
    }
  };

  const handlePurchase = async (skin: Skin) => {
    if (!session) return;
    setPurchasing(skin.id);
    try {
      const { checkoutUrl } = await purchaseSkin(session.accessToken, skin.id);
      // Redirect to checkout
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to start purchase:', error);
      setPurchasing(null);
    }
  };

  const handlePreviewPlanChange = (plan: HipercubePlan) => {
    setPreviewPlan(plan);
  };

  if (status !== 'signed-in') {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-24 text-center">
        <HipercubeLottie plan="gold" size={120} className="mx-auto mb-8" />
        <h1 className="text-3xl font-bold text-cream mb-4">{t.skinNFT.title}</h1>
        <p className="text-muted mb-8 max-w-xl mx-auto">{t.skinNFT.subtitle}</p>
        <div className="flex items-center justify-center gap-4">
          <a
            href="/conta"
            className="pill pressable bg-gold px-6 py-2.5 text-[14px] font-medium text-ink hover:bg-gold-bright"
          >
            {t.chrome.signIn}
          </a>
          <a
            href="/conta"
            className="pill pressable border border-line-strong px-6 py-2.5 text-[14px] font-medium text-cream hover:bg-white/10"
          >
            {t.pricing.signInToBuy}
          </a>
        </div>
        <p className="mt-6 text-sm text-muted">{t.skinNFT.syncMessage}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
      {/* Header */}
      <header className="mb-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-cream tracking-tight">{t.skinNFT.title}</h1>
            <p className="mt-2 text-muted">{t.skinNFT.subtitle}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted shrink-0">
            <Sparkles className="size-4 text-gold" aria-hidden="true" />
            <span>{t.skinNFT.syncMessage}</span>
          </div>
        </div>
      </header>

      {/* Preview Section */}
      <section className="mb-12" aria-labelledby="preview-heading">
        <h2 id="preview-heading" className="sr-only">{t.skinNFT.previewTitle}</h2>
        <div className="rounded-2xl border border-line bg-ink/50 p-6 sm:p-8">
          <div className="flex items-center justify-between gap-4 mb-6">
            <h3 className="text-lg font-semibold text-cream">{t.skinNFT.previewTitle}</h3>
            <div className="flex items-center gap-2" role="radiogroup" aria-label="Preview plan">
              {(['default', 'silver', 'gold'] as HipercubePlan[]).map(plan => (
                <button
                  key={plan}
                  onClick={() => handlePreviewPlanChange(plan)}
                  role="radio"
                  aria-checked={previewPlan === plan}
                  className={`pill pressable px-3 py-1.5 text-xs font-medium transition-all ${
                    previewPlan === plan
                      ? 'bg-gold text-ink'
                      : 'text-muted hover:text-cream border border-line'
                  }`}
                >
                  {plan.charAt(0).toUpperCase() + plan.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="relative aspect-square max-w-xs mx-auto">
            <HipercubeLottie
              plan={previewPlan}
              size="full"
              className="relative z-10"
              title={`${t.skinNFT.previewTitle} - ${previewPlan}`}
            />
            {/* Overlay preview of equipped accessories */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              {SLOT_ORDER.map(slot => {
                const skinId = equipped[slot];
                if (!skinId) return null;
                const skin = allSkins.find(s => s.id === skinId);
                if (!skin) return null;
                return (
                  <div key={slot} className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={skin.previewUrl}
                      alt=""
                      className="size-24 sm:size-32 opacity-90"
                      style={{
                        // Position offsets for each slot - these would be refined with actual anchor data
                        transform: getSlotOffset(slot),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-4 text-center text-sm text-muted">{t.skinNFT.animationNote}</p>
        </div>
      </section>

      {/* Owned Skins */}
      <section className="mb-12" aria-labelledby="owned-heading">
        <h2 id="owned-heading" className="text-xl font-semibold text-cream mb-6 flex items-center gap-3">
          <Package className="size-5 text-gold" aria-hidden="true" />
          {t.skinNFT.owned}
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted">{t.skinNFT.loading}</div>
        ) : hasOwnedSkins(skinsBySlot) ? (
          <div className="space-y-8">
            {SLOT_ORDER.map(slot => {
              const slotSkins = skinsBySlot[slot].filter(s => s.isOwned);
              if (slotSkins.length === 0) return null;
              return (
                <SlotSection
                  key={slot}
                  slot={slot}
                  skins={slotSkins}
                  equipped={equipped[slot]}
                  onEquip={handleEquip}
                  equipping={equipping}
                />
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-line bg-ink/50 p-12 text-center">
            <Package className="size-12 text-muted mx-auto mb-4" aria-hidden="true" />
            <p className="text-muted">{t.skinNFT.emptyOwned}</p>
            <a
              href="#available"
              className="mt-4 inline-block pill pressable bg-gold px-5 py-2 text-sm font-medium text-ink hover:bg-gold-bright"
            >
              {t.skinNFT.available}
            </a>
          </div>
        )}
      </section>

      {/* Available Skins */}
      <section id="available" aria-labelledby="available-heading">
        <h2 id="available-heading" className="text-xl font-semibold text-cream mb-6 flex items-center gap-3">
          <ShoppingBag className="size-5 text-gold" aria-hidden="true" />
          {t.skinNFT.available}
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted">{t.skinNFT.loading}</div>
        ) : hasAvailableSkins(skinsBySlot, userSkins) ? (
          <div className="space-y-8">
            {SLOT_ORDER.map(slot => {
              const slotSkins = skinsBySlot[slot].filter(s => !s.isOwned);
              if (slotSkins.length === 0) return null;
              return (
                <SlotSection
                  key={slot}
                  slot={slot}
                  skins={slotSkins}
                  equipped={null}
                  onEquip={handlePurchase}
                  equipping={purchasing}
                  isShop={true}
                />
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-line bg-ink/50 p-12 text-center">
            <ShoppingBag className="size-12 text-muted mx-auto mb-4" aria-hidden="true" />
            <p className="text-muted">{t.skinNFT.emptyAvailable}</p>
          </div>
        )}
      </section>
    </div>
  );
};

function hasOwnedSkins(skinsBySlot: Record<SkinSlot, SkinWithStatus[]>): boolean {
  return SLOT_ORDER.some(slot => skinsBySlot[slot].some(s => s.isOwned));
}

function hasAvailableSkins(skinsBySlot: Record<SkinSlot, SkinWithStatus[]>, userSkins: readonly UserSkinInventory[]): boolean {
  const ownedIds = new Set(userSkins.map(s => s.skinId));
  return SLOT_ORDER.some(slot => skinsBySlot[slot].some(s => !ownedIds.has(s.id)));
}

interface SlotSectionProps {
  slot: SkinSlot;
  skins: SkinWithStatus[];
  equipped: string | null;
  onEquip: (skin: SkinWithStatus) => void;
  equipping: string | null;
  isShop?: boolean;
}

function SlotSection({ slot, skins, equipped, onEquip, equipping, isShop = false }: SlotSectionProps) {
  const { t } = useI18n();
  const Icon = SLOT_ICONS[slot];
  const labelKey = SLOT_LABEL_KEYS[slot];

  return (
    <section aria-labelledby={`slot-${slot}-heading`} className="space-y-4">
      <h3 id={`slot-${slot}-heading`} className="flex items-center gap-2 text-lg font-medium text-cream">
        <Icon className="size-5 text-gold" aria-hidden="true" />
        <span>{t.skinNFT[labelKey]}</span>
        <span className="text-xs text-muted uppercase tracking-wider">{skins.length}</span>
      </h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {skins.map(skin => (
          <SkinCard
            key={skin.id}
            skin={skin}
            isEquipped={equipped === skin.id}
            onAction={onEquip}
            loading={equipping === skin.id}
            isShop={isShop}
          />
        ))}
      </div>
    </section>
  );
}

interface SkinCardProps {
  skin: SkinWithStatus;
  isEquipped: boolean;
  onAction: (skin: SkinWithStatus) => void;
  loading: boolean;
  isShop?: boolean;
}

function SkinCard({ skin, isEquipped, onAction, loading, isShop = false }: SkinCardProps) {
  const { t } = useI18n();
  const rarityColor = RARITY_COLORS[skin.rarity];
  const rarityBg = RARITY_BG[skin.rarity];

  return (
    <article className={`relative rounded-xl border ${rarityBg} bg-ink/50 p-4 transition-all hover:border-gold/30`}>
      {/* Rarity badge */}
      <div className="absolute top-3 right-3">
        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${rarityColor} ${rarityBg}`}>
          {t.skinNFT[`rarity${skin.rarity.charAt(0).toUpperCase() + skin.rarity.slice(1)}` as keyof typeof t.skinNFT]}
        </span>
      </div>

      {/* Preview */}
      <div className="aspect-square mb-4 relative overflow-hidden rounded-lg bg-ink/30 flex items-center justify-center">
        {skin.previewUrl.endsWith('.lottie') ? (
          <HipercubeLottie plan="default" size={80} className="opacity-80" />
        ) : (
          <img
            src={skin.previewUrl}
            alt=""
            className="max-w-full max-h-full object-contain"
            loading="lazy"
          />
        )}
      </div>

      {/* Info */}
      <h4 className="font-semibold text-cream mb-1 truncate">{skin.name}</h4>
      <p className="text-xs text-muted mb-3 line-clamp-2">{skin.description}</p>

      {/* Tags */}
      {skin.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3" aria-label="Tags">
          {skin.tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 text-[10px] text-muted bg-white/5 rounded">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Action */}
      <div className="flex items-center justify-between pt-3 border-t border-line">
        {isEquipped ? (
          <span className="px-2.5 py-1 text-xs font-medium text-gold bg-gold/10 rounded-full">
            <Check className="size-3 inline-block mr-1" aria-hidden="true" />
            {t.skinNFT.equippedBadge}
          </span>
        ) : isShop ? (
          <button
            onClick={() => onAction(skin)}
            disabled={loading}
            className="w-full pill pressable bg-gold px-3 py-1.5 text-xs font-medium text-ink hover:bg-gold-bright disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-1.5">
                <svg className="animate-spin size-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                {t.skinNFT.purchase}
              </span>
            ) : (
              t.skinNFT.purchase
            )}
          </button>
        ) : (
          <button
            onClick={() => onAction(skin)}
            disabled={loading}
            className="w-full pill pressable border border-line-strong px-3 py-1.5 text-xs font-medium text-cream hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-1.5">
                <svg className="animate-spin size-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                {isEquipped ? t.skinNFT.unequip : t.skinNFT.equip}
              </span>
            ) : (
              isEquipped ? t.skinNFT.unequip : t.skinNFT.equip
            )}
          </button>
        )}
      </div>
    </article>
  );
}

// Helper to get visual offset for each slot in the preview
function getSlotOffset(slot: SkinSlot): string {
  const offsets: Record<SkinSlot, string> = {
    hat: 'translate(-50%, -120%)',
    glasses: 'translate(-50%, -10%)',
    eye: 'translate(-50%, -10%)',
    nose: 'translate(-50%, 20%)',
    mouth: 'translate(-50%, 50%)',
    body: 'translate(-50%, 0%)',
  };
  return offsets[slot] || 'translate(-50%, 0%)';
}